import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface JobRecord {
  id: number;
  job_code: string;
  job_title: string;
  job_family: string | null;
  grade: string | null;
}

interface JobForm {
  job_code: string;
  job_title: string;
  job_family: string;
  grade: string;
}

const emptyForm: JobForm = { job_code: '', job_title: '', job_family: '', grade: '' };

export function Jobs() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<JobRecord[] | null>(null);
  const [editing, setEditing] = useState<'new' | JobRecord | null>(null);
  const [form, setForm] = useState<JobForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<JobRecord[]>('/jobs').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: JobRecord) => {
    setForm({
      job_code: item.job_code,
      job_title: item.job_title,
      job_family: item.job_family ?? '',
      grade: item.grade ?? '',
    });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof JobForm) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/jobs', form);
      else if (editing) await api.put(`/jobs/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: JobRecord) => {
    if (!(await confirmDelete(`Delete "${item.job_title}"?`))) return;
    try {
      await api.delete(`/jobs/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Jobs</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Job Family</th>
                <th>Grade</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td className="muted">{item.job_code}</td>
                  <td>{item.job_title}</td>
                  <td className="muted">{item.job_family ?? '—'}</td>
                  <td className="muted">{item.grade ?? '—'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No jobs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add job' : 'Edit job'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Job Code *
              <input value={form.job_code} onChange={set('job_code')} required />
              {errors.job_code && <small className="error">{errors.job_code[0]}</small>}
            </label>
            <label>
              Job Title *
              <input value={form.job_title} onChange={set('job_title')} required />
              {errors.job_title && <small className="error">{errors.job_title[0]}</small>}
            </label>
            <label>
              Job Family
              <input value={form.job_family} onChange={set('job_family')} />
              {errors.job_family && <small className="error">{errors.job_family[0]}</small>}
            </label>
            <label>
              Grade
              <input value={form.grade} onChange={set('grade')} />
              {errors.grade && <small className="error">{errors.grade[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
